import "./soiltexture.css";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import appConfig from "../../config/config";

const SOIL_TYPES = {
  Clay: {
    clay: { min: 40, max: 100 },
    silt: { min: 0, max: 40 },
    sand: { min: 0, max: 45 },
    color: "#8B4513"
  },
  "Silty Clay": {
    clay: { min: 40, max: 60 },
    silt: { min: 40, max: 60 },
    sand: { min: 0, max: 20 },
    color: "#A0522D"
  },
  "Sandy Clay": {
    clay: { min: 35, max: 55 },
    silt: { min: 0, max: 20 },
    sand: { min: 45, max: 65 },
    color: "#6B4423"
  },
  "Clay Loam": {
    clay: { min: 27, max: 40 },
    silt: { min: 15, max: 52 },
    sand: { min: 20, max: 45 },
    color: "#8B6914"
  },
  "Silty Clay Loam": {
    clay: { min: 27, max: 40 },
    silt: { min: 40, max: 73 },
    sand: { min: 0, max: 20 },
    color: "#966919"
  },
  "Sandy Clay Loam": {
    clay: { min: 20, max: 35 },
    silt: { min: 0, max: 28 },
    sand: { min: 45, max: 80 },
    color: "#8B7355"
  },
  Loam: {
    clay: { min: 7, max: 27 },
    silt: { min: 28, max: 50 },
    sand: { min: 23, max: 52 },
    color: "#DAA520"
  },
  "Silty Loam": {
    clay: { min: 0, max: 27 },
    silt: { min: 50, max: 88 },
    sand: { min: 0, max: 50 },
    color: "#CD853F"
  },
  "Sandy Loam": {
    clay: { min: 0, max: 20 },
    silt: { min: 0, max: 50 },
    sand: { min: 43, max: 85 },
    color: "#DEB887"
  },
  Silt: {
    clay: { min: 0, max: 12 },
    silt: { min: 80, max: 100 },
    sand: { min: 0, max: 20 },
    color: "#D2B48C"
  },
  "Loamy Sand": {
    clay: { min: 0, max: 15 },
    silt: { min: 0, max: 30 },
    sand: { min: 70, max: 90 },
    color: "#F4A460"
  },
  Sand: {
    clay: { min: 0, max: 10 },
    silt: { min: 0, max: 15 },
    sand: { min: 85, max: 100 },
    color: "#FFD700"
  }
};

const clamp = (val) => Math.max(0, Math.min(100, Math.round(val)));

export default function SoilTexture({ initialComposition, onSoilChange }) {
  const { t } = useTranslation();
  const svgRef = useRef(null);

  const [composition, setComposition] = useState({ clay: 33, silt: 33, sand: 34 });
  const [pending, setPending] = useState({ clay: 33, silt: 33, sand: 34 });
  const [isDragging, setIsDragging] = useState(false);

  const initializedRef = useRef(false);
  const prevCompRef = useRef();

  const A = { x: 50, y: 0 };
  const B = { x: 0, y: 86.6 };
  const C = { x: 100, y: 86.6 };

  const triangleGrid = `${appConfig.ROUTER_BASE_PATH}/Griglia_STT.png`;

  const calculateSoilType = (comp) => {
    const matches = [];

    for (const [soilTypeName, ranges] of Object.entries(SOIL_TYPES)) {
      if (
        comp.clay >= ranges.clay.min &&
        comp.clay <= ranges.clay.max &&
        comp.silt >= ranges.silt.min &&
        comp.silt <= ranges.silt.max &&
        comp.sand >= ranges.sand.min &&
        comp.sand <= ranges.sand.max
      ) {
        matches.push(soilTypeName);
      }
    }

    if (matches.includes("Sandy Loam") && matches.includes("Loamy Sand")) return "Loamy Sand";
    if (matches.includes("Loamy Sand") && matches.includes("Sand")) return "Sand";
    if (matches.includes("Silty Loam") && matches.includes("Silt")) return "Silt";

    return matches[0] || "Unknown";
  };

  const barycentricToSoil = (px, py) => {
    const v0 = [C.x - A.x, C.y - A.y];
    const v1 = [B.x - A.x, B.y - A.y];
    const v2 = [px - A.x, py - A.y];

    const dot00 = v0[0] ** 2 + v0[1] ** 2;
    const dot01 = v0[0] * v1[0] + v0[1] * v1[1];
    const dot02 = v0[0] * v2[0] + v0[1] * v2[1];
    const dot11 = v1[0] ** 2 + v1[1] ** 2;
    const dot12 = v1[0] * v2[0] + v1[1] * v2[1];

    const denom = dot00 * dot11 - dot01 * dot01;
    if (denom === 0) return null;

    const u = (dot11 * dot02 - dot01 * dot12) / denom;
    const v = (dot00 * dot12 - dot01 * dot02) / denom;
    const w = 1 - u - v;

    const clay = clamp(w * 100);
    const sand = clamp(v * 100);
    const silt = clamp(u * 100);
    const total = clay + sand + silt;

    return {
      clay,
      sand,
      silt: silt + (100 - total)
    };
  };

  const soilToCoords = ({ clay, sand, silt }) => {
    const total = clay + silt + sand || 1;
    const a = clay / total;
    const b = sand / total;
    const c = silt / total;

    return {
      x: a * A.x + b * B.x + c * C.x,
      y: a * A.y + b * B.y + c * C.y
    };
  };

  const getSvgCoords = (clientX, clientY) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const pt = svgRef.current.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svgRef.current.getScreenCTM();
    if (!ctm) return pt;
    return pt.matrixTransform(ctm.inverse());
  };

  useEffect(() => {
    if (!initializedRef.current && initialComposition) {
      let clay = Number(initialComposition.clay) || 0;
      let silt = Number(initialComposition.silt) || 0;
      let sand = Number(initialComposition.sand) || 0;

      const total = clay + silt + sand;

      if (total === 0) {
        clay = 33;
        silt = 33;
        sand = 34;
      } else if (total !== 100) {
        clay = Math.round((clay / total) * 100);
        silt = Math.round((silt / total) * 100);
        sand = 100 - clay - silt;
      }

      const comp = { clay, silt, sand };
      setComposition(comp);
      setPending(comp);
      initializedRef.current = true;
    }
  }, [initialComposition]);

  useEffect(() => {
    const prev = prevCompRef.current;
    if (!prev || prev.clay !== composition.clay || prev.silt !== composition.silt || prev.sand !== composition.sand) {
      prevCompRef.current = composition;
      if (typeof onSoilChange === "function") {
        onSoilChange({
          type: calculateSoilType(composition),
          ...composition
        });
      }
    }
  }, [composition, onSoilChange]);

  const handleMove = useCallback((e) => {
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);

    if (clientX == null || clientY == null) return;

    const { x, y } = getSvgCoords(clientX, clientY);
    if (!isInsideTriangle(x, y, A, B, C)) return;

    const newComp = barycentricToSoil(x, y);
    if (newComp) {
      setComposition(newComp);
      setPending(newComp);
    }
  }, []);

  const handleUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleUp);

      return () => {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleUp);
      };
    }
  }, [isDragging, handleMove, handleUp]);

  function isInsideTriangle(px, py, A, B, C) {
    const area = (a, b, c) => Math.abs((a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y)) / 2);
    const total = area(A, B, C);
    const a1 = area({ x: px, y: py }, B, C);
    const a2 = area(A, { x: px, y: py }, C);
    const a3 = area(A, B, { x: px, y: py });
    return Math.abs(total - (a1 + a2 + a3)) < 0.5;
  }

  const handleDown = (e) => {
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (clientX == null || clientY == null) return;

    const { x, y } = getSvgCoords(clientX, clientY);
    if (isInsideTriangle(x, y, A, B, C)) {
      const newComp = barycentricToSoil(x, y);
      if (newComp) {
        setComposition(newComp);
        setPending(newComp);
        setIsDragging(true);
        if (e.touches) e.preventDefault();
      }
    }
  };

  const handleInputChange = (field) => (e) => {
    setPending(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleBlur = () => {
    const clay = parseInt(pending.clay, 10) || 0;
    const silt = parseInt(pending.silt, 10) || 0;
    const sand = parseInt(pending.sand, 10) || 0;
    const total = clay + silt + sand;

    if (total === 100) {
      const updated = { clay, silt, sand };
      setComposition(updated);
      setPending(updated);
    }
  };

  const { x, y } = soilToCoords(composition);
  const totalPending = (parseInt(pending.clay, 10) || 0) + (parseInt(pending.silt, 10) || 0) + (parseInt(pending.sand, 10) || 0);

  return (
    <>
      <div className="soil-texture-header">
        <h5>
          {t("soiltexture:labels.soilTexture")} <span className="soil-type-value">{calculateSoilType(composition)}</span>
        </h5>
      </div>

      <div className="soil-triangle-container">
        <svg ref={svgRef} viewBox="0 0 100 100" className="triangle-svg" onMouseDown={handleDown} onTouchStart={handleDown}>
          <image href={triangleGrid} x="-25" y="-25" width="150" height="150" />
          <circle cx={x} cy={y} r={2.5} fill="#1a5f3c" stroke="#fff" strokeWidth={0.8} />
        </svg>

        <div className="soil-inputs">
          <h5>{t("soiltexture:labels.composition")}</h5>

          {["clay","silt","sand"].map((type) => (
            <label key={type} className="soil-input-label">
              {t(`soiltexture:labels.${type}`)}
              <input type="number" value={pending[type]} onChange={handleInputChange(type)} onBlur={handleBlur} min="0" max="100" />
            </label>
          ))}

          {totalPending !== 100 && (
            <p style={{ color: "#c00", fontSize: "0.9rem" }}>
              {t("soiltexture:alerts.alertTotalSum")}
            </p>
          )}
        </div>
      </div>
    </>
  );
}